import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  useUserContext,
  useVerificationStatusFromContext
} from "@/context/user-context";
import { cn, verificationStatusIcons } from "@/utilities/app-utils";
import {
  layout,
  typography,
  verification
} from "@/utilities/tailwind-utils";

export function HeaderVerification() {
  const { user } = useUserContext();
  const verificationStatus = useVerificationStatusFromContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const userType = user?.userType;
  const isPracticeOrProfessional =
    userType === "practice" || userType === "professional";

  const status = verificationStatus ?? "pending";

  const config = useMemo(
    () => verification.getStatusConfig(status),
    [status]
  );
  const themeClasses = useMemo(
    () =>
      config ? verification.getThemeClasses(config.theme) : null,
    [config]
  );

  const goToVerification = useCallback(() => {
    if (!isPracticeOrProfessional) {
      return;
    }
    const base = userType === "practice" ? "/practice" : "/professional";
    navigate(`${base}/verification`);
    setOpen(false);
  }, [isPracticeOrProfessional, navigate, userType]);

  if (!isPracticeOrProfessional || !userType) {
    return null;
  }

  const IconComponent = verificationStatusIcons[status];

  return (
    <div className="flex min-w-0 shrink-0 items-center">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex max-h-8 min-h-0 min-w-0 items-center gap-1.5 rounded-md border-0 bg-transparent p-0",
              "text-left outline-none",
              "hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              "touch-manipulation"
            )}
            aria-label={config?.title ?? "Verification status"}
          >
            <span className="relative shrink-0">
              <span
                className={cn(
                  "flex size-7 items-center justify-center rounded-full",
                  themeClasses?.iconBg ?? "bg-muted"
                )}
              >
                <IconComponent
                  className={cn(
                    "size-3.5",
                    themeClasses?.icon ?? "text-muted-foreground"
                  )}
                />
              </span>
              {status !== "approved" ? (
                <span className="absolute right-0 top-0 size-1.5 rounded-full bg-destructive ring-2 ring-background" />
              ) : null}
            </span>
            {config && themeClasses ? (
              <span
                className={cn(
                  typography.textSmallBold14,
                  themeClasses.title,
                  "hidden min-w-0 max-w-[min(14rem,28vw)] truncate sm:inline"
                )}
              >
                {config.title}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          sideOffset={8}
          className="w-[min(calc(100vw-1.5rem),20rem)] max-w-[min(calc(100vw-1.5rem),20rem)] p-4"
        >
          {config && themeClasses ? (
            <div className={cn(layout.flexColGap3)}>
              <div className={cn(layout.flexCenterGap3)}>
                <div
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-full",
                    themeClasses.iconBg
                  )}
                >
                  <IconComponent
                    className={cn("size-5", themeClasses.icon)}
                  />
                </div>
                <div className={cn(layout.flexColGap1, "min-w-0 flex-1")}>
                  <p
                    className={cn(
                      typography.textSmallBold14,
                      themeClasses.title
                    )}
                  >
                    {config.title}
                  </p>
                  <p
                    className={cn(
                      typography.textSmallRegular12,
                      themeClasses.description
                    )}
                  >
                    {config.description}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                className="w-full touch-manipulation"
                onClick={goToVerification}
              >
                {config.action}
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
